import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import SteppsIndexes from "./components/SteppsIndexes";
import VideoSelection from "./Steps/VideoSelection";
import VideoSourceSelection from "./Steps/VideoSourceSelection";
import RephrasedContentReview from "./Steps/RephrasedContentReview";
import DestinationSelection from "./Steps/DestinationSelection";
import { useNavigate } from "react-router-dom";
import api from "../../conf/api";

/**
 * Main MRSS Management Wizard
 * Multi-step workflow for uploading videos to MRSS feeds
 */
function Index() {
  const nagigation = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [disableButtons, setDisableButtons] = useState({
    next: false,
    back: true,
  });

  const initialState = {
    selectedVideos: [],
    videoSources: {}, // { unique_key: { source: 'ORIGINAL' | 'LOCAL' | 'EDITED', file: File, renderedUrl: string } }
    videoPlatforms: {}, // { unique_key: ["YOUTUBE", "TIKTOK", ...] }
    videoConvertToVertical: {}, // { unique_key: boolean }
    rephrasedContent: {}, // { unique_key: { title, description, keywords } }
    destinations: {
      feeds: [],
      youtube: false,
      youtubeChannels: [],
      subbrands: [],
    },
  };

  const [uploadData, setUploadData] = useState(initialState);
  const [hasSocialFeeds, setHasSocialFeeds] = useState(false);

  // Check if any SOCIAL feeds exist in the system
  useEffect(() => {
    const checkSocialFeeds = async () => {
      try {
        const response = await api.get("/mrss/feeds/", {
          params: { feed_type: "SOCIAL", page_size: 1 },
        });
        const results = response.data.results || response.data;
        setHasSocialFeeds(Array.isArray(results) ? results.length > 0 : false);
      } catch (error) {
        console.error("Error checking social feeds:", error);
      }
    };
    checkSocialFeeds();
  }, []);

  // Step 1: Video Selection Handlers
  const handleVideoSelect = (videos) => {
    setUploadData((prev) => ({
      ...prev,
      selectedVideos: videos,
    }));
  };

  const handleAddVideo = (video) => {
    if (
      !uploadData.selectedVideos.find((v) => v.unique_key === video.unique_key)
    ) {
      setUploadData((prev) => ({
        ...prev,
        selectedVideos: [...prev.selectedVideos, video],
      }));
    }
  };

  const handleRemoveVideo = (video) => {
    setUploadData((prev) => ({
      ...prev,
      selectedVideos: prev.selectedVideos.filter(
        (v) => v.unique_key !== video.unique_key
      ),
    }));
  };

  // Step 2: Video Source Handlers
  const handleVideoSourceChange = (uniqueKey, sourceData) => {
    setUploadData((prev) => ({
      ...prev,
      videoSources: {
        ...prev.videoSources,
        [uniqueKey]: sourceData,
      },
    }));
  };

  const handlePlatformChange = (uniqueKey, platforms) => {
    setUploadData((prev) => ({
      ...prev,
      videoPlatforms: {
        ...prev.videoPlatforms,
        [uniqueKey]: platforms,
      },
    }));
  };

  const handleConvertToVerticalChange = (uniqueKey, value) => {
    setUploadData((prev) => ({
      ...prev,
      videoConvertToVertical: {
        ...prev.videoConvertToVertical,
        [uniqueKey]: value,
      },
    }));
  };

  const handleVideoSelection = (uniqueKey, data) => {
    setUploadData((prev) => ({
      ...prev,
      selectedVideos: prev.selectedVideos.map((vid) =>
        vid.unique_key === uniqueKey ? { ...vid, ...data } : vid
      ),
    }));
  };

  const handleThumbnailChange = (uniqueKey, base64) => {
    setUploadData((prev) => ({
      ...prev,
      selectedVideos: prev.selectedVideos.map((vid) =>
        vid.unique_key === uniqueKey ? { ...vid, thumbnail: base64 } : vid
      ),
    }));
  };

  // Step 3: Rephrased Content Handler
  const handleContentApproved = (rephrasedContent) => {
    setUploadData((prev) => ({
      ...prev,
      rephrasedContent,
    }));
    // Auto-advance to next step
    setActiveStep(3);
  };

  // Step 4: Destination Selection Handlers
  const handleDestinationChange = (destinations) => {
    setUploadData((prev) => ({
      ...prev,
      destinations,
    }));
  };

  const Reset = () => {
    setUploadData(initialState);
    setDisableButtons({
      next: false,
      back: true,
    });
    setActiveStep(0);
    nagigation("/mrss/queue");
  };

  const steps = [
    {
      label: "Select Videos",
      Content: () => (
        <VideoSelection
          selectedVideos={uploadData.selectedVideos}
          handleVideoSelect={handleVideoSelect}
          handleAddVideo={handleAddVideo}
          handleRemoveVideo={handleRemoveVideo}
        />
      ),
    },
    {
      label: "Choose Video Source",
      Content: () => (
        <VideoSourceSelection
          selectedVideos={uploadData.selectedVideos}
          videoSources={uploadData.videoSources}
          handleVideoSourceChange={handleVideoSourceChange}
          handleThumbnailChange={handleThumbnailChange}
          handleVideoSelection={handleVideoSelection}
          videoPlatforms={uploadData.videoPlatforms}
          handlePlatformChange={handlePlatformChange}
          hasSocialFeeds={hasSocialFeeds}
          videoConvertToVertical={uploadData.videoConvertToVertical}
          handleConvertToVerticalChange={handleConvertToVerticalChange}
        />
      ),
    },
    {
      label: "Review AI & SEO Content",
      Content: () => (
        <RephrasedContentReview
          selectedVideos={uploadData.selectedVideos}
          onContentApproved={handleContentApproved}
          onBack={() => setActiveStep(1)}
        />
      ),
    },
    {
      label: "Select Publishing Destinations",
      Content: () => (
        <DestinationSelection
          selectedVideos={uploadData.selectedVideos}
          videoSources={uploadData.videoSources}
          rephrasedContent={uploadData.rephrasedContent}
          destinations={uploadData.destinations}
          handleDestinationChange={handleDestinationChange}
          onReset={Reset}
          videoPlatforms={uploadData.videoPlatforms}
          videoConvertToVertical={uploadData.videoConvertToVertical}
        />
      ),
    },
  ];

  useEffect(() => {
    // Disable/enable buttons based on selections
    if (activeStep === 0) {
      setDisableButtons({
        next: uploadData.selectedVideos.length === 0,
        back: true,
      });
    } else if (activeStep === 1) {
      const allHaveSource = uploadData.selectedVideos.every((video) => {
        const src = uploadData.videoSources[video.unique_key];
        if (!src) return false;
        if (src.source === "LOCAL") return !!src.file;
        if (src.source === "EDITED") return !!src.renderedUrl || !!src.renderPayload;
        return src.source === "ORIGINAL";
      });
      // If social feeds exist, all videos must have at least one platform selected
      const allHavePlatforms =
        !hasSocialFeeds ||
        uploadData.selectedVideos.every(
          (video) => uploadData.videoPlatforms[video.unique_key]?.length > 0
        );
      setDisableButtons({
        next: !(allHaveSource && allHavePlatforms),
        back: false,
      });
    } else if (activeStep === 2) {
      // Rephrasing step - managed internally by component
      setDisableButtons({
        next: true,
        back: true,
      });
    } else if (activeStep === 3) {
      // Destination selection - final step
      setDisableButtons({
        next: true,
        back: true,
      });
    }
  }, [activeStep, uploadData, hasSocialFeeds]);

  return (
    <Box sx={{ width: "100%", padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        MRSS Feed Video Publishing
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Upload and distribute videos across multiple brands and platforms
      </Typography>

      <SteppsIndexes
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={steps}
        disableButtons={disableButtons}
        onReset={Reset}
      />
    </Box>
  );
}

export default Index;
